import { Button, ButtonGroup } from '@mui/material';
import { Port, RelayState, RelayStatus } from '@thingslog/repositories';
import React, { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export const RelayButtonGroup: FC<RelayBtnGroupProps> = ({
  status,
  ports,
  isDisabled,
  onChange,
}: RelayBtnGroupProps) => {
  const { t } = useTranslation();
  const currStatus = useMemo(() => status.relayState, [status]);
  const label = useMemo(() => {
    if (status && ports) {
      const port = ports[status.sensorIndex];
      if (port) {
        return port.sensor.name;
      } else {
        return `Sensor: ${status.sensorIndex}`;
      }
    } else if (status) {
      return `Sensor: ${status.sensorIndex}`;
    } else {
      return '';
    }
  }, [status, ports]);

  const showOnButton = useMemo(() => {
    switch (status.buttonArrangement) {
      case 'ON_OFF':
      case 'ON_OFF_AUTO':
      case 'ON_OFF_TOGGLE':
        return true;
      default:
        throw new Error(
          `Invalid button arrangement arrangement (ON) for sensor ${status.sensorIndex}`
        );
    }
  }, [status]);

  const showOffButton = useMemo(() => {
    switch (status.buttonArrangement) {
      case 'ON_OFF':
      case 'ON_OFF_AUTO':
      case 'ON_OFF_TOGGLE':
        return true;
      default:
        throw new Error(
          `Invalid button arrangement arrangement (OFF) for sensor ${status.sensorIndex}`
        );
    }
  }, [status]);

  const showAutoButton = useMemo(() => {
    switch (status.buttonArrangement) {
      case 'ON_OFF':
      case 'ON_OFF_TOGGLE':
        return false;
      case 'ON_OFF_AUTO':
        return true;
      default:
        throw new Error(
          `Invalid button arrangement arrangement (AUTO) for sensor ${status.sensorIndex}`
        );
    }
  }, [status]);

  const showToggleButton = useMemo(() => {
    switch (status.buttonArrangement) {
      case 'ON_OFF':
      case 'ON_OFF_AUTO':
        return false;
      case 'ON_OFF_TOGGLE':
        return true;
      default:
        throw new Error(
          `Invalid button arrangement arrangement (TOGGLE) for sensor ${status.sensorIndex}`
        );
    }
  }, [status]);

  const handleButtonClick = (sensorIndex: number, state: RelayState): void => {
    onChange(sensorIndex, state);
  };

  const handleOnButtonClick = useCallback(() => {
    handleButtonClick(status.sensorIndex, 'ON');
  }, [status]);

  const handleOffButtonClick = useCallback(() => {
    handleButtonClick(status.sensorIndex, 'OFF');
  }, [status]);

  const handleAutoButtonClick = useCallback(() => {
    handleButtonClick(status.sensorIndex, 'AUTO');
  }, [status]);

  const handleToggleButtonClick = useCallback(() => {
    handleButtonClick(status.sensorIndex, 'TOGGLE');
  }, [status]);

  return (
    <div className="flex w-full flex-col items-center justify-center lg:w-auto">
      <span className="text-sm font-semibold text-gray-800">{label}</span>
      <ButtonGroup
        variant="outlined"
        size="small"
        disabled={isDisabled}
        className="w-full lg:w-auto"
        sx={{
          '& .MuiButton-root': { flex: '1 1 0', minWidth: 0 },
        }}
      >
        {showOnButton && (
          <Button
            variant={currStatus === 'ON' ? 'contained' : undefined}
            onClick={handleOnButtonClick}
          >
            {t('on_button')}
          </Button>
        )}
        {showOffButton && (
          <Button
            variant={currStatus === 'OFF' ? 'contained' : undefined}
            onClick={handleOffButtonClick}
          >
            {t('off_button')}
          </Button>
        )}
        {showAutoButton && (
          <Button
            variant={currStatus === 'AUTO' ? 'contained' : undefined}
            onClick={handleAutoButtonClick}
          >
            {t('auto_button')}
          </Button>
        )}
        {showToggleButton && (
          <Button
            variant={currStatus === 'TOGGLE' ? 'contained' : undefined}
            onClick={handleToggleButtonClick}
          >
            {t('toggle_button')}
          </Button>
        )}
      </ButtonGroup>
    </div>
  );
};

interface RelayBtnGroupProps {
  status: RelayStatus;
  ports?: Record<number, Port>;
  isDisabled: boolean;
  onChange: (sensorIndex: number, status: RelayState) => void;
}
